'use client'

import { useCallback, JSX } from 'react'

import { compact, orderBy } from 'lodash'
import { EllipsisVertical, KeyRound, ShieldCheck } from 'lucide-react'

import { withAuthentication } from '~/components/auth/withAuthentication'
import {
  List,
  PageLayout,
  Pill,
  Tooltip,
  UserDisplay,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { Dropdown } from '~/components/component-library/Dropdown'
import { InviteMemberDialogContent } from '~/components/settings/InviteMemberDialogContent'
import { RemoveMemberDialogContent } from '~/components/settings/RemoveMemberDialogContent'
import {
  SettingsPageContainer,
  settingsVisibility,
} from '~/components/settings/utils'
import { SignerIcon } from '~/components/smart-accounts/SignerIcon'
import { useAuth } from '~/context/AuthProvider'
import {
  useActiveOrg,
  useActiveOrgMember,
  useActiveOrgUpdates,
} from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useServerRequests } from '~/hooks/useRequests'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  AuthOrg,
  OrgInvitation,
  OrgMember,
  OrgMemberRole,
  OrgSigner,
  OrgSmartAccount,
} from '~/types'
import { getSmartAccountName } from '~/utils/smartAccounts'
import { cn } from '~/utils/tailwind-utils'

const TeamMembersPageInner = () => {
  return (
    <SettingsPageContainer
      requireOwner={settingsVisibility.members === 'owners'}
    >
      <PageLayout
        pageType="standard"
        title="Members"
        description="Manage your workspace members and invitations."
      >
        <OrgMembersEditList displayPasskeyIndicator />
      </PageLayout>
    </SettingsPageContainer>
  )
}

const OrgMembersEditList = ({
  displayPasskeyIndicator = false,
}: {
  displayPasskeyIndicator?: boolean
}): JSX.Element => {
  const { org: activeOrg } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const { user: authUser } = useAuthUser()
  const { smartAccounts } = useSmartAccounts()

  const isCurrentUserOwner = orgMember?.role === 'OWNER'

  const membersAndInvitations = activeOrg
    ? getOrgMembersAndInvitations(activeOrg)
    : []

  const items = membersAndInvitations.map((memberOrInvitation) => {
    const isInvitation = memberOrInvitation.type === 'invitation'
    const isMember = memberOrInvitation.type === 'member'
    const isMemberAuthUser = isMember && authUser?.id === memberOrInvitation.id
    const isOwner = isMember && memberOrInvitation.role === OrgMemberRole.Owner
    const isRegularMember =
      isMember && memberOrInvitation.role === OrgMemberRole.Member

    return {
      key: memberOrInvitation.id,
      content: (
        <UserDisplay user={memberOrInvitation} layout="standard" size="base" />
      ),
      meta: (
        <div className="flex items-center gap-2">
          <span className="text-xs">
            {!isInvitation && memberOrInvitation.role === 'OWNER' && (
              <Pill size="base" label="Owner" />
            )}
            {isInvitation && (
              <Pill
                size="base"
                label={`Invited as ${memberOrInvitation.role.toLowerCase()}`}
              />
            )}
          </span>
          {displayPasskeyIndicator &&
            (isMember ? (
              <>
                <OrgMemberPasskeysIndicator
                  passkeys={memberOrInvitation.passkeys}
                />
                <OrgMemberSmartAccountsIndicator
                  memberId={memberOrInvitation.id}
                  smartAccounts={smartAccounts}
                />
              </>
            ) : (
              <div className="h-4 w-4" />
            ))}
        </div>
      ),
      actionIcon: isCurrentUserOwner && (
        <Dropdown
          items={compact([
            isOwner && {
              content: 'Downgrade to member...',
              onClick: () =>
                setGlobalModalState({
                  isOpen: true,
                  content: (
                    <ChangeRoleDialogContent
                      member={memberOrInvitation}
                      targetRole={OrgMemberRole.Member}
                      closeDialog={closeGlobalModal}
                    />
                  ),
                }),
            },
            isRegularMember && {
              content: 'Upgrade to owner...',
              onClick: () =>
                setGlobalModalState({
                  isOpen: true,
                  content: (
                    <ChangeRoleDialogContent
                      member={memberOrInvitation}
                      targetRole={OrgMemberRole.Owner}
                      closeDialog={closeGlobalModal}
                    />
                  ),
                }),
            },
            isMember && {
              content: 'Remove from workspace...',
              onClick: () =>
                setGlobalModalState({
                  isOpen: true,
                  content: (
                    <RemoveMemberDialogContent
                      member={memberOrInvitation}
                      closeDialog={closeGlobalModal}
                    />
                  ),
                }),
            },
            isInvitation && {
              content: 'Revoke invitation',
              onClick: () =>
                setGlobalModalState({
                  isOpen: true,
                  content: (
                    <RevokeInvitationDialogContent
                      invitation={memberOrInvitation}
                      closeDialog={closeGlobalModal}
                    />
                  ),
                }),
            },
          ])}
        >
          <button
            type="button"
            disabled={isMemberAuthUser}
            className={cn({ invisible: isMemberAuthUser })}
          >
            <EllipsisVertical size={16} />
          </button>
        </Dropdown>
      ),
    }
  })

  return (
    <List
      items={items}
      cta={{
        label: 'Invite member',
        isDisabled: !isCurrentUserOwner,
        tooltip: !isCurrentUserOwner
          ? 'Only workspace owners can invite members'
          : undefined,
        onClick: () => {
          setGlobalModalState({
            isOpen: true,
            content: (
              <InviteMemberDialogContent closeDialog={closeGlobalModal} />
            ),
          })
        },
      }}
    />
  )
}

export const RevokeInvitationDialogContent = ({
  invitation,
  closeDialog,
  onRevoke,
}: {
  invitation: OrgInvitation
  closeDialog: () => void
  onRevoke?: () => void
}) => {
  const { org } = useActiveOrg()
  const { removeInvitation } = useActiveOrgUpdates()
  const { serverDeleteRequest, isProcessing } = useServerRequests()

  const revokeInvitation = useCallback(async () => {
    const response = await serverDeleteRequest({
      path: `/api/org/user/invite/${invitation.id}`,
      errorMessage: 'Failed to revoke invite',
      successMessage: 'Invitation revoked',
    })

    if (response) {
      removeInvitation(invitation.id)
      onRevoke?.()
      closeDialog()
    }
  }, [
    closeDialog,
    invitation.id,
    removeInvitation,
    serverDeleteRequest,
    onRevoke,
  ])

  return (
    <SplitsDialogContent
      id="revokeInvitation"
      variant="destructive"
      title={`Revoke ${invitation.email} invitation?`}
      isLoading={isProcessing}
      deleteButton={{
        text: 'Revoke',
        onClick: revokeInvitation,
        eventProperties: { id: invitation.id, email: invitation.email },
      }}
    >
      <p>
        <span className="font-semibold">{invitation.email}</span>&apos;s
        invitation to join the{' '}
        <span className="font-semibold">{org?.name}</span> workspace will be
        revoked.
      </p>
    </SplitsDialogContent>
  )
}

const ChangeRoleDialogContent = ({
  member,
  targetRole,
  closeDialog,
}: {
  member: OrgMember
  targetRole: OrgMemberRole
  closeDialog: () => void
}) => {
  const { serverPutRequest, isProcessing } = useServerRequests()
  const { org } = useActiveOrg()
  const { updateOrgMember } = useActiveOrgUpdates()

  const updateRole = async () => {
    const result = await serverPutRequest({
      path: '/api/org/user/role',
      params: {
        orgId: org!.id,
        memberId: member.id,
        role: targetRole,
      },
      errorMessage: 'Failed to update member role',
    })
    if (result) {
      updateOrgMember({ ...member, role: targetRole })
      closeDialog()
    }
  }

  return (
    <SplitsDialogContent
      id="updateMemberRole"
      variant="confirmation"
      title={
        targetRole === OrgMemberRole.Member
          ? `Convert ${member.email} to member?`
          : targetRole === OrgMemberRole.Owner
            ? `Convert ${member.email} to owner?`
            : ''
      }
      isLoading={isProcessing}
      confirmButton={{
        text: 'Confirm',
        onClick: updateRole,
        eventName:
          targetRole === OrgMemberRole.Member
            ? 'convertTeamMemberToMember'
            : targetRole === OrgMemberRole.Owner
              ? 'convertTeamMemberToOwner'
              : '',
        eventProperties: { email: member.email },
      }}
    >
      {targetRole === OrgMemberRole.Member ? (
        <p>
          They will no longer be able to perform owner actions on the{' '}
          <span>{org?.name}</span> workspace
        </p>
      ) : targetRole === OrgMemberRole.Owner ? (
        <p>
          They will have full control of the <span>{org?.name}</span> workspace
        </p>
      ) : (
        <></>
      )}
    </SplitsDialogContent>
  )
}

const OrgMemberPasskeysIndicator = ({
  passkeys,
}: {
  passkeys: OrgSigner[]
}) => {
  return passkeys.length === 0 ? (
    <Tooltip content="No passkeys added">
      <KeyRound className="text-orange-500" size={16} />
    </Tooltip>
  ) : (
    <Tooltip
      content={
        <div className="flex flex-col gap-2">
          <div className="text-xs text-secondary">Available signers</div>
          {passkeys.map((passkey) => (
            <div key={passkey.id} className="flex flex-row items-center gap-2">
              <SignerIcon signer={passkey} />
              <div>{passkey.name}</div>
            </div>
          ))}
        </div>
      }
    >
      <KeyRound size={16} />
    </Tooltip>
  )
}

const OrgMemberSmartAccountsIndicator = ({
  memberId,
  smartAccounts,
}: {
  memberId: string
  smartAccounts: OrgSmartAccount[]
}) => {
  const memberSmartAccounts = smartAccounts.filter((smartAccount) =>
    smartAccount.signers.some((signer) => signer.user?.id === memberId),
  )

  if (memberSmartAccounts.length === 0) {
    return (
      <Tooltip content="Not a signer on any accounts">
        <ShieldCheck className="text-orange-500" size={16} />
      </Tooltip>
    )
  }

  return (
    <Tooltip
      content={
        <div className="flex max-w-48 flex-col gap-2">
          <div className="text-xs text-secondary">Active accounts</div>
          {memberSmartAccounts.map((smartAccount) => (
            <div key={smartAccount.id} className="truncate">
              {getSmartAccountName(smartAccount)}
            </div>
          ))}
        </div>
      }
    >
      <ShieldCheck size={16} />
    </Tooltip>
  )
}

const getOrgMembersAndInvitations = (org: AuthOrg) => {
  const passkeys = org.passkeys

  return orderBy(
    [
      ...org.members.map((member) => {
        const memberPasskeys = passkeys.filter(
          (passkey) => passkey.user?.id === member.id,
        )

        return {
          ...member,
          type: 'member' as const,
          passkeys: memberPasskeys,
        }
      }),
      ...org.invitations.map((member) => ({
        ...member,
        type: 'invitation' as const,
        passkeys: [],
      })),
    ],
    [
      (memberOrInvitation) =>
        memberOrInvitation.type === 'invitation'
          ? 0
          : memberOrInvitation.role === OrgMemberRole.Owner
            ? 2
            : 1,
      (memberOrInvitation) => new Date(memberOrInvitation.createdAt),
    ],
    ['desc', 'asc'],
  )
}
export const TeamMembersPage = withAuthentication(TeamMembersPageInner)
